import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { BarChart3, CheckCircle, AlertTriangle, TrendingUp } from 'lucide-react';
import { MONTHS_RO } from './constants';

export const KpiDashboardView = ({ data, year }) => {
  if (!data) return <div className="flex-1 flex items-center justify-center text-slate-400">Se incarca...</div>;
  const { overall, sections, monthly_trend, top_problematic } = data;
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="kpi-view">
      <h2 className="text-lg font-bold text-slate-900">Dashboard KPI - {year}</h2>

      {/* Overall stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total intrari', value: overall.total, bg: 'bg-blue-50', text: 'text-blue-700', icon: BarChart3 },
          { label: 'Validate', value: overall.validated, bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle },
          { label: 'Restante', value: overall.overdue, bg: 'bg-red-50', text: 'text-red-700', icon: AlertTriangle },
          { label: 'Completare', value: `${overall.completion_pct}%`, bg: 'bg-purple-50', text: 'text-purple-700', icon: TrendingUp },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl border p-4 ${s.bg}`}>
            <div className="flex items-center gap-1.5 mb-1"><s.icon className={`w-4 h-4 ${s.text}`} /><span className="text-xs text-slate-500">{s.label}</span></div>
            <span className={`text-2xl font-bold ${s.text}`}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Monthly trend bar chart */}
      <div className="border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Progres lunar</h3>
        <div className="flex items-end gap-1.5 h-32">
          {monthly_trend.map(m => {
            const pct = m.total > 0 ? (m.validated / m.total * 100) : 0;
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-slate-100 rounded-t relative" style={{ height: '100px' }}>
                  <div className="absolute bottom-0 w-full bg-emerald-500 rounded-t transition-all" style={{ height: `${pct}%` }} />
                </div>
                <span className="text-[10px] text-slate-500">{MONTHS_RO[m.month - 1]?.slice(0, 3)}</span>
                <span className="text-[10px] font-semibold text-slate-600">{m.validated}/{m.total}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Per section */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Completare per sectie</h3>
          <div className="space-y-2">
            {sections.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-slate-700 w-24 truncate font-medium">{s.section}</span>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${s.completion_pct > 70 ? 'bg-emerald-500' : s.completion_pct > 30 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${s.completion_pct}%` }} />
                </div>
                <span className="text-xs font-semibold text-slate-600 w-12 text-right">{s.completion_pct}%</span>
                <Badge className="bg-slate-100 text-slate-600 text-[10px]">{s.equipment_count} ut.</Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Top problematic */}
        <div className="border rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-red-500" />Top utilaje problematice
          </h3>
          {top_problematic.length === 0 ? (
            <div className="text-center py-6"><CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" /><p className="text-xs text-slate-500">Nicio restanta!</p></div>
          ) : (
            <div className="space-y-2">
              {top_problematic.map((eq, i) => (
                <div key={i} className="flex items-center justify-between bg-red-50/50 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-sm font-medium text-slate-800">{eq.equipment_name}</span>
                    <p className="text-[10px] text-slate-500">{eq.section}</p>
                  </div>
                  <Badge className="bg-red-100 text-red-700">{eq.overdue} restante</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
